import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import SortableTable from 'components/sortable-table/SortableTable';
import Tooltip from 'components/Tooltip';
import AddNew from 'components/buttons/add-new/AddNew';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { withModal } from 'components/modal/ModalHOC';

import SOVModalGeneric from 'modules/insights/sov/components/modals/generic/SOVModalGeneric';
import SOVQueriesModal from 'modules/insights/sov/components/queries/modals/SOVQueriesModal';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { COLLABARY, CREATE, DELETE, DUPLICATE, EDIT, FIXED_TO_TOP, QUERY } from 'modules/insights/sov/utils/sov';

import { getColumnIcons, getColumnKeyword } from 'modules/insights/sov/utils/tables/sov-tables';
import {
  getColumnCheckbox,
  getColumnHashtag,
  getColumnVideoDescription,
  getColumnVideoTitle
} from 'modules/insights/sov/utils/tables/youtube/sov-tables-youtube';
import {
  ICONS_MAP,
  enrichQueryIcon,
  getIconDelete,
  getIconDuplicate,
  getIconEdit
} from 'modules/insights/sov/utils/icons/sov-icons';

import './sov-queries-collabary-youtube.scss';

import type { SVGDynamicIconProps, SortableTableColumnType } from 'types';
import type {
  SOVGeneralProps,
  SOVIconEnriched,
  SOVQueryEndpoint,
  SOVQueryLoading,
  SOVQuerySave,
  SOVReport
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('modules/insights/sov/components/queries/sov-queries.i18n.json'));

type OwnProps = {
  report: SOVReport,
  isActionEvent: boolean,
  onUpdateReport: (a: SOVGeneralProps) => void,
  onSaveQuery: (a: SOVQuerySave) => void,
  onUpdateQuery: (b: SOVQueryEndpoint, a: boolean) => void,
  onDuplicateQuery: (a: SOVGeneralProps) => void,
  onDeleteQuery: (a: SOVGeneralProps) => void
};

type StateProps = {
  queries: Array<SOVQueryEndpoint>,
  isLoadingQueries: SOVQueryLoading
};

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  dispatch: any // added by connect
} & OwnProps & StateProps;

const AddNewWithQueriesModal = withModal(AddNew, {
  modalContent: SOVQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithQueriesModal = withModal(Tooltip, {
  modalContent: SOVQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithGenericModal = withModal(Tooltip, {
  modalContent: SOVModalGeneric,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_queries';

class SOVQueriesCollabaryYoutube extends React.PureComponent<Props> {
  getIconEdit(query: SOVQueryEndpoint): SOVIconEnriched {
    const { report } = this.props;
    const icon = getIconEdit();

    return enrichQueryIcon(query, report, icon);
  }

  getIconDuplicate(query: SOVQueryEndpoint): SOVIconEnriched {
    const { report, onDuplicateQuery } = this.props;
    const icon = getIconDuplicate(onDuplicateQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getIconDelete(query: SOVQueryEndpoint): SOVIconEnriched {
    const { report, onDeleteQuery } = this.props;
    const icon = getIconDelete(onDeleteQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getModalTitle(type: string): string {
    return __t(`${MSG_KEY}.labels.${type}`);
  }

  getModalLabels(type: string): SOVGeneralProps {
    return {
      actionButton: __t(`${MSG_KEY}.buttons.${type}`)
    };
  }

  getQueryData(): Array<SOVQueryEndpoint> {
    const { queries } = this.props;

    return queries.map(query => ({
      ...query,
      icons: [
        this.getIconEdit(query),
        this.getIconDuplicate(query),
        this.getIconDelete(query)
      ]
    }));
  }

  getColumns(): Array<SortableTableColumnType> {
    const { queries } = this.props;

    return [
      ...insertIf(!isEmpty(queries), ...[
        getColumnCheckbox(this.handleToggleQuery, 'sov-queries-collabary-youtube__column-status'),
        getColumnKeyword(MSG_KEY, 'sov-queries-collabary-youtube__column-keyword'),
        getColumnHashtag(this.renderColumnTitle, this.renderColumnContents),
        getColumnVideoTitle(this.renderColumnTitle, this.renderColumnContents),
        getColumnVideoDescription(this.renderColumnTitle, this.renderColumnContents),
        getColumnIcons(this.renderIconModal, 'sov-queries-collabary-youtube__icons')
      ])
    ];
  }

  handleToggleQuery = (event: SyntheticEvent<HTMLInputElement>, query: SOVQueryEndpoint): void => {
    const { onUpdateQuery } = this.props;

    const updatedQuery = {
      ...query,
      enabled: !query.enabled
    };

    onUpdateQuery(updatedQuery, false);
  };

  renderColumnTitle = (columnName: string): JSX.Element => (
    <div className="sov-queries-collabary-youtube__column-title">
      {__t(`${MSG_KEY}.labels.${columnName}`)}
    </div>
  );

  renderColumnContents = (query: SOVQueryEndpoint, columnType: string): React.ReactNode | undefined | null => {
    const keywords = (query.includedKeywords || []).filter(item => item.type === columnType);

    if (isEmpty(keywords)) {
      return null;
    }

    return (
      <div className="sov-queries-collabary-youtube__column-contents">
        <SVGDynamicIcon
          name="checkmark-custom-fg"
          width={12}
          height={10}
          customColor={query.enabled ? 'primary' : 'grey-light'}
        />
      </div>
    );
  };

  renderIconModal = (icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode | undefined | null => {
    if (icon.name === ICONS_MAP.edit) {
      return this.renderModalEdit(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.duplicate) {
      return this.renderModalDuplicate(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.delete) {
      return this.renderModalDelete(icon, iconProps);
    }

    return null;
  };

  renderModalEdit(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <IconWithQueriesModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          report,
          query: icon.query,
          pageType: COLLABARY,
          sectionType: QUERY,
          actionType: EDIT,
          onSaveQuery
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithQueriesModal>
    );
  }

  renderModalDuplicate(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DUPLICATE),
          labels: this.getModalLabels(DUPLICATE),
          message: __t(`${MSG_KEY}.messages.${DUPLICATE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DUPLICATE,
          onAction: icon.onDuplicate
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderModalDelete(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DELETE),
          labels: this.getModalLabels(DELETE),
          message: __t(`${MSG_KEY}.messages.${DELETE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DELETE,
          onAction: icon.onDelete
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-queries-collabary-youtube__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderAddNew(): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <div className="sov-queries-collabary-youtube__add-new">
        <AddNewWithQueriesModal
          text={__t(`${SHARED_MSG_KEY}.labels.add_new`)}
          tooltip={__t(`${MSG_KEY}.tooltips.add_new`)}
          modalContentProps={{
            report,
            query: {},
            pageType: COLLABARY,
            sectionType: QUERY,
            actionType: CREATE,
            onSaveQuery
          }}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16
          }}
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="sov-queries-collabary-youtube__header">
        <div className="sov-queries-collabary-youtube__header-text">
          {__t(`${MSG_KEY}.labels.queries`)}
        </div>
        {this.renderAddNew()}
      </div>
    );
  }

  renderTableOverlay(): React.ReactNode {
    return (
      <div className="sov-queries-collabary-youtube__table-overlay">
        {this.renderSpinner()}
      </div>
    );
  }

  renderTable(): React.ReactNode {
    const { isLoadingQueries, isActionEvent } = this.props;
    const queryData = this.getQueryData();
    const sortedQueryData = orderBy(queryData, 'id', 'desc');

    return (
      <React.Fragment>
        {
          isLoadingQueries && isActionEvent &&
            this.renderTableOverlay()
        }
        <SortableTable
          data={sortedQueryData}
          columns={this.getColumns()}
          layout="simple"
          maxHeight={286}
        />
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { isLoadingQueries, isActionEvent } = this.props;

    return (
      <div className="sov-queries-collabary-youtube">
        <div className="sov-queries-collabary-youtube__list">
          {this.renderHeader()}
          <div className="sov-queries-collabary-youtube__table">
            {
              isLoadingQueries && !isActionEvent
                ? this.renderSpinner()
                : this.renderTable()
            }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ sovQueriesYoutube }): StateProps => ({
  queries: sovQueriesYoutube.queries,
  isLoadingQueries: sovQueriesYoutube.isLoading
});

export default connect(mapStateToProps)(SOVQueriesCollabaryYoutube);
